import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { satsueiMemoCategoryService } from '../services/satsueiMemoCategoryService.js'
import { useApiError } from '../../shared/hooks/index.js'
import { useSatsueiMemoCategory } from '../hooks/index.js'
import MessageDialog from '../../shared/ui/MessageDialog.jsx'
import Button from '../../shared/ui/Button.jsx'
import Layout from '../components/Layout.jsx'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// Sortable Category Item Component using @dnd-kit
const SortableCategoryItem = ({ category, isSelected, onSelect, onUpdateTemp, onUpdateFinal, onCancelEdit, onDelete, tempCategoryNames }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.satsueiMemoCategoryId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  // Get the current display value (temp if editing, otherwise actual)
  const displayValue = tempCategoryNames[category.satsueiMemoCategoryId] !== undefined 
    ? tempCategoryNames[category.satsueiMemoCategoryId] 
    : category.satsueiMemoCategoryName

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // Finalize the edit on Enter
      onUpdateFinal(category.satsueiMemoCategoryId, displayValue)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      // Cancel the edit on Escape
      onCancelEdit(category.satsueiMemoCategoryId)
    }
  }

  const handleBlur = () => {
    // Finalize the edit on blur
    onUpdateFinal(category.satsueiMemoCategoryId, displayValue)
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-2 sm:p-3 border rounded-lg cursor-pointer transition-colors ${
        isSelected
          ? 'border-red-500 bg-red-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={() => onSelect(category.satsueiMemoCategoryId)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <div 
            {...attributes} 
            {...listeners}
            className="cursor-move text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            ⋮⋮
          </div>
          <input
            id={`category-name-${category.satsueiMemoCategoryId}`}
            data-testid={`category-name-${category.satsueiMemoCategoryId}`}
            type="text"
            value={displayValue}
            onChange={(e) => onUpdateTemp(category.satsueiMemoCategoryId, e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="flex-1 bg-transparent border-none focus:outline-none min-w-0 text-sm sm:text-base"
            disabled={category.satsueiMemoCategoryId === 0}
          />
        </div>
        <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
          <Button
            id={`category-config-${category.satsueiMemoCategoryId}`}
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onSelect(category.satsueiMemoCategoryId)
            }}
            className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap"
          >
            内容設定
          </Button>
          {category.satsueiMemoCategoryId !== 0 && (
            <Button
              id={`category-delete-${category.satsueiMemoCategoryId}`}
              variant="danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                onDelete(category.satsueiMemoCategoryId)
              }}
              className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 p-0"
            >
              ×
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

// Sortable Column Item Component using @dnd-kit
const SortableColumnItem = ({ 
  column, 
  selectedCategory, 
  onUpdateTemp,
  onUpdateFinal,
  onCancelEdit,
  onDelete, 
  onConfig, 
  isExpanded,
  getColumnTypeName,
  newColumnValueName,
  setNewColumnValueName,
  onAddColumnValue,
  onUpdateColumnValue,
  onDeleteColumnValue,
  onRestoreColumnValue,
  onUpdateColumnInitialValue,
  categories,
  setCategories,
  setSuccessMessage,
  setShowSuccessDialog,
  tempColumnNames
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.satsueiMemoColumnId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  // Get the current display value (temp if editing, otherwise actual)
  const getDisplayValue = () => {
    const key = `${selectedCategory.satsueiMemoCategoryId}-${column.satsueiMemoColumnId}`
    return tempColumnNames[key] !== undefined 
      ? tempColumnNames[key] 
      : column.satsueiMemoColumnName
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // Finalize the edit on Enter
      onUpdateFinal(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId, getDisplayValue(), column.requiredFlg)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      // Cancel the edit on Escape
      onCancelEdit(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId)
    }
  }

  const handleBlur = () => {
    // Finalize the edit on blur
    onUpdateFinal(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId, getDisplayValue(), column.requiredFlg)
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="p-2 sm:p-3 border border-gray-200 rounded-lg"
    >
      {/* Mobile Layout - Stacked */}
      <div className="md:hidden space-y-3">
        {/* Row 1: Drag handle and column name */}
        <div className="flex items-center space-x-2">
          <div 
            {...attributes} 
            {...listeners}
            className="cursor-move text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            ⋮⋮
          </div>
          <input
            id={`column-name-mobile-${column.satsueiMemoColumnId}`}
            type="text"
            value={getDisplayValue()}
            onChange={(e) => onUpdateTemp(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId, e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="flex-1 bg-transparent border-none focus:outline-none text-sm min-w-0"
          />
        </div>
        
        {/* Row 2: Column type and required checkbox */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 truncate max-w-24 sm:max-w-32">
            {getColumnTypeName(parseInt(column.columnType))}
          </span>
          {parseInt(column.columnType) !== 1 && (
            <label htmlFor={`column-required-mobile-${column.satsueiMemoColumnId}`} className="flex items-center space-x-1">
              <input
                id={`column-required-mobile-${column.satsueiMemoColumnId}`}
                type="checkbox"
                checked={column.requiredFlg === 1}
                onChange={(e) => {
                  onUpdateFinal(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId, getDisplayValue(), e.target.checked ? 1 : 0)
                }}
                className="rounded"
              />
              <span className="text-xs sm:text-sm">必須</span>
            </label>
          )}
        </div>
        
        {/* Row 3: Action buttons */}
        <div className="flex items-center justify-end space-x-2">
          {[2, 3, 4, 5, 6, 7, 8, 11, 12, 13, 14, 17].includes(parseInt(column.columnType)) && (
            <Button
              id={`column-config-mobile-${column.satsueiMemoColumnId}`}
              variant="outline"
              size="sm"
              onClick={() => onConfig(
                selectedCategory.satsueiMemoCategoryId,
                column.satsueiMemoColumnId,
                column.columnType
              )}
              className="whitespace-nowrap text-xs px-2 sm:px-3 w-14 sm:w-16"
            >
              {[2, 3, 4, 17].includes(parseInt(column.columnType))
                ? isExpanded
                  ? '閉じる'
                  : '初期値'
                : isExpanded
                  ? '閉じる'
                  : '設定'
              }
            </Button>
          )}
          <Button
            id={`column-delete-mobile-${column.satsueiMemoColumnId}`}
            variant="danger"
            size="sm"
            onClick={() => onDelete(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId)}
            className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 p-0"
          >
            ×
          </Button>
        </div>
      </div>

      {/* Tablet/Desktop Layout - Grid */}
      <div className="hidden md:grid md:grid-cols-12 md:gap-2 md:items-center">
        {/* Drag Handle */}
        <div className="col-span-1 flex items-center justify-center">
          <div 
            {...attributes} 
            {...listeners}
            className="cursor-move text-gray-400 hover:text-gray-600"
          >
            ⋮⋮
          </div>
        </div>

        {/* Column Name */}
        <div className="col-span-4 flex items-center">
          <input
            id={`column-name-desktop-${column.satsueiMemoColumnId}`}
            data-testid={`column-name-desktop-${column.satsueiMemoColumnId}`}
            type="text"
            value={getDisplayValue()}
            onChange={(e) => onUpdateTemp(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId, e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="w-full bg-transparent border-none focus:outline-none text-sm"
          />
        </div>

        {/* Column Type */}
        <div className="col-span-3 flex items-center">
          <span className="text-sm text-gray-500">
            {getColumnTypeName(parseInt(column.columnType))}
          </span>
        </div>

        {/* Required Checkbox */}
        <div className="col-span-2 flex items-center justify-center">
          {parseInt(column.columnType) !== 1 && (
            <label htmlFor={`column-required-desktop-${column.satsueiMemoColumnId}`} className="flex items-center space-x-1">
              <input
                id={`column-required-desktop-${column.satsueiMemoColumnId}`}
                data-testid={`column-required-desktop-${column.satsueiMemoColumnId}`}
                type="checkbox"
                checked={column.requiredFlg === 1}
                onChange={(e) => {
                  onUpdateFinal(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId, getDisplayValue(), e.target.checked ? 1 : 0)
                }}
                className="rounded"
              />
              <span className="text-sm">必須</span>
            </label>
          )}
        </div>

        {/* Action Buttons */}
        <div className="col-span-2 flex items-center justify-end space-x-1">
          {[2, 3, 4, 5, 6, 7, 8, 11, 12, 13, 14, 17].includes(parseInt(column.columnType)) && (
            <Button
              id={`column-config-desktop-${column.satsueiMemoColumnId}`}
              variant="outline"
              size="sm"
              onClick={() => onConfig(
                selectedCategory.satsueiMemoCategoryId,
                column.satsueiMemoColumnId,
                column.columnType
              )}
              className="whitespace-nowrap text-xs px-2 w-16"
            >
              {[2, 3, 4, 17].includes(parseInt(column.columnType))
                ? isExpanded
                  ? '閉じる'
                  : '初期値'
                : isExpanded
                  ? '閉じる'
                  : '設定'
              }
            </Button>
          )}
          <Button
            id={`column-delete-desktop-${column.satsueiMemoColumnId}`}
            variant="danger"
            size="sm"
            onClick={() => onDelete(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId)}
            className="flex-shrink-0 w-8 h-8 p-0"
          >
            ×
          </Button>
        </div>
      </div>

      {/* Column Values Section - Show only when expanded for selection-based columns */}
      {[5, 6, 7, 8, 11, 12, 13, 14].includes(parseInt(column.columnType)) && isExpanded && (
        <div className="col-span-12 mt-3 p-2 sm:p-3 bg-gray-50 rounded">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-2">
            <h4 className="text-sm font-medium text-gray-700">選択肢項目</h4>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                id={`new-column-value-${column.satsueiMemoColumnId}`}
                type="text"
                placeholder="新しい選択肢"
                value={newColumnValueName}
                onChange={(e) => setNewColumnValueName(e.target.value)}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newColumnValueName.trim()) {
                    const activeValueCount = column.satsueiMemoColumnValueList?.filter(val => val.deleteFlg === 0).length || 0
                    const columnType = parseInt(column.columnType)
                    if (columnType === 11 && activeValueCount >= 2) {
                      return // Don't add if 2段階選択 already has 2 values
                    }
                    if (columnType === 12 && activeValueCount >= 3) {
                      return // Don't add if 3段階選択 already has 3 values
                    }
                    if (columnType === 13 && activeValueCount >= 5) {
                      return // Don't add if 5段階選択 already has 5 values
                    }
                    if (columnType === 14 && activeValueCount >= 10) {
                      return // Don't add if 10段階選択 already has 10 values
                    }
                    onAddColumnValue(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId)
                  }
                }}
              />
              <Button
                id={`add-column-value-${column.satsueiMemoColumnId}`}
                variant="outline"
                size="sm"
                onClick={() => onAddColumnValue(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId)}
                disabled={
                  !newColumnValueName.trim() || 
                  (parseInt(column.columnType) === 11 && 
                   (column.satsueiMemoColumnValueList?.filter(val => val.deleteFlg === 0).length || 0) >= 2) ||
                  (parseInt(column.columnType) === 12 && 
                   (column.satsueiMemoColumnValueList?.filter(val => val.deleteFlg === 0).length || 0) >= 3) ||
                  (parseInt(column.columnType) === 13 && 
                   (column.satsueiMemoColumnValueList?.filter(val => val.deleteFlg === 0).length || 0) >= 5) ||
                  (parseInt(column.columnType) === 14 && 
                   (column.satsueiMemoColumnValueList?.filter(val => val.deleteFlg === 0).length || 0) >= 10)
                }
                className="w-full sm:w-auto"
              >
                追加
              </Button>
            </div>
          </div>

          {/* Active Column Values */}
          <div className="space-y-1">
            {column.satsueiMemoColumnValueList?.filter(val => val.deleteFlg === 0).map(value => (
              <div key={value.satsueiMemoColumnValueId} className="flex flex-col sm:flex-row sm:items-center justify-between p-2 bg-white border rounded gap-2">
                <input
                  id={`column-value-name-${value.satsueiMemoColumnValueId}`}
                  data-testid={`column-value-name-${value.satsueiMemoColumnValueId}`}
                  type="text"
                  value={value.satsueiMemoColumnValueName}
                  onChange={(e) => {
                    onUpdateColumnValue(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId, value.satsueiMemoColumnValueId, e.target.value, value.initialValueFlg)
                  }}
                  className="flex-1 bg-transparent border-none focus:outline-none text-sm min-w-0"
                />
                <div className="flex items-center justify-between sm:justify-end space-x-2">
                  <label htmlFor={`column-value-initial-${value.satsueiMemoColumnValueId}`} className="flex items-center space-x-1">
                    <input
                      id={`column-value-initial-${value.satsueiMemoColumnValueId}`}
                      data-testid={`column-value-initial-${value.satsueiMemoColumnValueId}`}
                      type="checkbox"
                      checked={value.initialValueFlg === 1}
                      onChange={(e) => {
                        onUpdateColumnValue(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId, value.satsueiMemoColumnValueId, value.satsueiMemoColumnValueName, e.target.checked ? 1 : 0)
                      }}
                      className="rounded"
                    />
                    <span className="text-xs">初期値</span>
                  </label>
                  <Button
                    id={`column-value-delete-${value.satsueiMemoColumnValueId}`}
                    variant="danger"
                    size="sm"
                    onClick={() => onDeleteColumnValue(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId, value.satsueiMemoColumnValueId)}
                    className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 p-0"
                  >
                    ×
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Deleted Column Values */}
          {column.satsueiMemoColumnValueList?.filter(val => val.deleteFlg === 1).length > 0 && (
            <div className="mt-2">
              <h5 className="text-xs font-medium text-gray-600 mb-1">削除済み選択肢</h5>
              <div className="space-y-1">
                {column.satsueiMemoColumnValueList?.filter(val => val.deleteFlg === 1).map(value => (
                  <div key={value.satsueiMemoColumnValueId} className="flex flex-col sm:flex-row sm:items-center justify-between p-2 bg-gray-100 border rounded gap-2">
                    <span className="text-sm text-gray-600 truncate flex-1">{value.satsueiMemoColumnValueName}</span>
                    <Button
                      id={`column-value-restore-${value.satsueiMemoColumnValueId}`}
                      variant="outline"
                      size="sm"
                      onClick={() => onRestoreColumnValue(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId, value.satsueiMemoColumnValueId)}
                      className="w-full sm:w-auto flex-shrink-0"
                    >
                      戻す
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Initial Value Display for text/number columns - Show only when expanded */}
      {[2, 3, 4, 17].includes(parseInt(column.columnType)) && isExpanded && (
        <div className="col-span-12 mt-3 p-2 sm:p-3 bg-gray-50 rounded">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">初期値設定</h4>
          </div>
          <div className="space-y-2">
            <input
              id={`column-initial-value-${column.satsueiMemoColumnId}`}
              data-testid={`column-initial-value-${column.satsueiMemoColumnId}`}
              type={parseInt(column.columnType) === 4 || parseInt(column.columnType) === 17 ? "number" : "text"}
              placeholder="初期値を入力してください"
              value={column.tempInitialValue !== undefined ? column.tempInitialValue : ''}
              onChange={(e) => {
                const value = e.target.value
                // Validate number input for number columns
                if ((parseInt(column.columnType) === 4 || parseInt(column.columnType) === 17) && value && !/^[-]?([1-9]\d*|0)(\.\d+)?$/.test(value)) {
                  return // Don't update if invalid number
                }
                // Update temporary value
                const updatedCategories = categories.map(cat => {
                  if (cat.satsueiMemoCategoryId === selectedCategory.satsueiMemoCategoryId) {
                    const updatedColumns = cat.satsueiMemoColumnList?.map(col => {
                      if (col.satsueiMemoColumnId === column.satsueiMemoColumnId) {
                        return {
                          ...col,
                          tempInitialValue: value
                        }
                      }
                      return col
                    }) || []
                    return {
                      ...cat,
                      satsueiMemoColumnList: updatedColumns
                    }
                  }
                  return cat
                })
                setCategories(updatedCategories)
              }}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
            />
            <div className="p-2 bg-blue-50 border border-blue-200 rounded">
              <span className="text-sm text-blue-700 break-words">
                現在の初期値: {column.initialValue || '未設定'}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:space-x-2">
              <Button
                id={`column-initial-value-clear-${column.satsueiMemoColumnId}`}
                variant="outline"
                size="sm"
                onClick={() => {
                  // Clear temporary value
                  const updatedCategories = categories.map(cat => {
                    if (cat.satsueiMemoCategoryId === selectedCategory.satsueiMemoCategoryId) {
                      const updatedColumns = cat.satsueiMemoColumnList?.map(col => {
                        if (col.satsueiMemoColumnId === column.satsueiMemoColumnId) {
                          return {
                            ...col,
                            tempInitialValue: undefined
                          }
                        }
                        return col
                      }) || []
                      return {
                        ...cat,
                        satsueiMemoColumnList: updatedColumns
                      }
                    }
                    return cat
                  })
                  setCategories(updatedCategories)
                }}
                className="w-full sm:w-auto order-2 sm:order-1"
              >
                クリア
              </Button>
              <Button
                id={`column-initial-value-save-${column.satsueiMemoColumnId}`}
                variant="danger"
                size="sm"
                onClick={() => {
                  // Validate and update the actual initial value
                  const tempValue = column.tempInitialValue !== undefined ? column.tempInitialValue : ''
                  
                  // Validate number input for number columns
                  if ((parseInt(column.columnType) === 4 || parseInt(column.columnType) === 17) && tempValue && !/^[-]?([1-9]\d*|0)(\.\d+)?$/.test(tempValue)) {
                    setSuccessMessage('数値の形式が正しくありません。')
                    setShowSuccessDialog(true)
                    return
                  }
                  
                  // Update the actual initial value
                  onUpdateColumnInitialValue(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId, tempValue)
                  
                  // Clear temporary value and force re-render
                  const updatedCategories = categories.map(cat => {
                    if (cat.satsueiMemoCategoryId === selectedCategory.satsueiMemoCategoryId) {
                      const updatedColumns = cat.satsueiMemoColumnList?.map(col => {
                        if (col.satsueiMemoColumnId === column.satsueiMemoColumnId) {
                          return {
                            ...col,
                            tempInitialValue: undefined,
                            initialValue: tempValue // Also update initialValue here to ensure immediate display
                          }
                        }
                        return col
                      }) || []
                      return {
                        ...cat,
                        satsueiMemoColumnList: updatedColumns
                      }
                    }
                    return cat
                  })
                  setCategories(updatedCategories)
                }}
                className="w-full sm:w-auto order-1 sm:order-2"
              >
                決定
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Column type definitions
const COLUMN_TYPES = [
    { id: 1, value: 'ラベル' },
    { id: 2, value: '文字列(1行)' },
    { id: 3, value: '文字列(複数行)' },
    { id: 4, value: '数値' },
    { id: 5, value: 'ラジオボタン' },
    { id: 6, value: 'チェックボックス' },
    { id: 7, value: '複数選択' },
    { id: 8, value: 'プルダウン' },
    { id: 9, value: '日付' },
    { id: 10, value: '日時' },
    { id: 11, value: '2段階選択' },
    { id: 12, value: '3段階選択' },
    { id: 13, value: '5段階選択' },
    { id: 14, value: '10段階選択' },
    { id: 15, value: '文字列(参照)' },
    { id: 16, value: '外部検索' },
    { id: 17, value: '小数点数値' }
]

const SatsueiMemoCategory = () => {
    const { handleApiError, handleApiResponse, showErrorDialog, errorMessage, closeErrorDialog } = useApiError()

    // Custom hook for satsuei memo category state management
    const {
        categories,
        selectedCategoryId,
        tempCategoryNames,
        tempColumnNames,
        activeCategories,
        deletedCategories,
        selectedCategory,
        activeColumns,
        deletedColumns,
        setCategories,
        setSelectedCategoryId,
        setSelectedColumnId,
        addCategory,
        updateCategory,
        updateCategoryTemp,
        cancelCategoryEdit,
        deleteCategory,
        restoreCategory,
        addColumn,
        updateColumn,
        updateColumnTemp,
        cancelColumnEdit,
        deleteColumn,
        restoreColumn,
        addColumnValue,
        deleteColumnValue,
        restoreColumnValue,
        updateColumnInitialValue,
        updateColumnValue,
        prepareNewCategoryData,
        prepareUpdatedCategoryData,
        prepareRemovedCategoryData,
        prepareNewColumnData,
        prepareUpdatedColumnData,
        prepareRemovedColumnData,
        prepareNewColumnValueData,
        prepareUpdatedColumnValueData,
        prepareRemovedColumnValueData,
    } = useSatsueiMemoCategory()

    // Modal states
    const [showAddCategoryModal, setShowAddCategoryModal] = useState(false)
    const [showDeletedCategoryModal, setShowDeletedCategoryModal] = useState(false)
    const [showDeletedColumnModal, setShowDeletedColumnModal] = useState(false)

    // Form states
    const [newCategoryName, setNewCategoryName] = useState('')
    const [newColumnName, setNewColumnName] = useState('')
    const [newColumnType, setNewColumnType] = useState('1')
    const [newColumnValueName, setNewColumnValueName] = useState('')

    // Success message state
    const [showSuccessDialog, setShowSuccessDialog] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')

    // Loading states
    const [isLoadingCategories, setIsLoadingCategories] = useState(false)
    const [isRegistering, setIsRegistering] = useState(false)

    // Column config states - track which columns have their values expanded
    const [expandedColumns, setExpandedColumns] = useState('')

    // @dnd-kit sensors with activation constraint to prevent accidental drags
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require 8px movement before drag starts
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    // Handle drag end for categories reordering
    const handleCategoryDragEnd = (event) => {
        const { active, over } = event

        if (active.id !== over?.id && over) {
            const oldIndex = activeCategories.findIndex(item => item.satsueiMemoCategoryId === active.id)
            const newIndex = activeCategories.findIndex(item => item.satsueiMemoCategoryId === over.id)

            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(activeCategories, oldIndex, newIndex)
                
        // Update the categories with new order and set orderIndex based on position
                const categoriesWithOrderIndex = newOrder.map((category, index) => ({
            ...category,
            orderIndex: index
        }))
        setCategories(categoriesWithOrderIndex)
            }
        }
    }

    // Handle drag end for columns reordering
    const handleColumnDragEnd = (event) => {
        const { active, over } = event

        if (active.id !== over?.id && over && selectedCategory) {
            const oldIndex = activeColumns.findIndex(item => item.satsueiMemoColumnId === active.id)
            const newIndex = activeColumns.findIndex(item => item.satsueiMemoColumnId === over.id)

            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(activeColumns, oldIndex, newIndex)
                
        // Update the columns in the selected category with new order and set orderIndex based on position
                const columnsWithOrderIndex = newOrder.map((column, index) => ({
                ...column,
                orderIndex: index
            }))
            
            const updatedCategories = categories.map(cat => {
                if (cat.satsueiMemoCategoryId === selectedCategory.satsueiMemoCategoryId) {
                    return {
                        ...cat,
                        satsueiMemoColumnList: columnsWithOrderIndex
                    }
                }
                return cat
            })
            setCategories(updatedCategories)
        }
        }
    }

    // Load categories function
    const loadCategories = useCallback(async () => {
        setIsLoadingCategories(true)
        try {
            const data = await satsueiMemoCategoryService.getSatsueiMemoCategoryList()
            if (data?.satsueiMemoCategoryList) {
                // Sort categories by orderIndex and ensure they have proper orderIndex values
                const sortedCategories = data.satsueiMemoCategoryList
                    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
                    .map((category, index) => ({
                        ...category,
                        orderIndex: category.orderIndex !== undefined ? category.orderIndex : index
                    }))
                setCategories(sortedCategories)
            }
        } catch (error) {
            handleApiError(error)
        } finally {
            setIsLoadingCategories(false)
        }
    }, [setCategories, handleApiError])

    // Load categories on component mount
    useEffect(() => {
      loadCategories()
  }, [loadCategories])


    // Register function
    const handleRegister = async () => {
        setIsRegistering(true)
        try {
            // Validate required values for columns that need them
            const missingValueColumns = []

            // Use the current categories state from the hook instead of categoryData
            categories.forEach(category => {
                if (category.deleteFlg === 0) {
                    category.satsueiMemoColumnList?.forEach(column => {
                        if (column.deleteFlg === 0) {
                            const columnType = parseInt(column.columnType)
                            if ([5, 6, 7, 8, 11, 12, 13, 14].includes(columnType)) {
                                const valueCount = column.satsueiMemoColumnValueList?.filter(v => v.deleteFlg === 0).length || 0
                                if (valueCount === 0) {
                                    missingValueColumns.push(`${category.satsueiMemoCategoryName} > ${column.satsueiMemoColumnName}`)
                                }
                            }
                        }
                    })
                }
            })

            if (missingValueColumns.length > 0) {
                const errorMessage = `以下の撮影メモ項目に項目値が登録されていません：\n${missingValueColumns.join('\n')}`
                throw new Error(errorMessage)
            }

            // Process each list separately
            const promises = []

            // Categories
            const newCategoryData = prepareNewCategoryData()
            const updatedCategoryData = prepareUpdatedCategoryData()
            const removedCategoryData = prepareRemovedCategoryData()

            if (newCategoryData.length > 0) {
                promises.push(satsueiMemoCategoryService.addSatsueiMemoCategoryList(newCategoryData))
            }
            if (updatedCategoryData.length > 0) {
                promises.push(satsueiMemoCategoryService.updateSatsueiMemoCategoryList(updatedCategoryData))
            }
            if (removedCategoryData.length > 0) {
                promises.push(satsueiMemoCategoryService.removeSatsueiMemoCategoryList(removedCategoryData))
            }

            // Columns
            const newColumnData = prepareNewColumnData()
            const updatedColumnData = prepareUpdatedColumnData()
            const removedColumnData = prepareRemovedColumnData()

            if (newColumnData.length > 0) {
                promises.push(satsueiMemoCategoryService.addSatsueiMemoColumnList(newColumnData))
            }
            if (updatedColumnData.length > 0) {
                promises.push(satsueiMemoCategoryService.updateSatsueiMemoColumnList(updatedColumnData))
            }
            if (removedColumnData.length > 0) {
                promises.push(satsueiMemoCategoryService.removeSatsueiMemoColumnList(removedColumnData))
            }

            // Column Values
            const newColumnValueData = prepareNewColumnValueData()
            const updatedColumnValueData = prepareUpdatedColumnValueData()
            const removedColumnValueData = prepareRemovedColumnValueData()

            if (newColumnValueData.length > 0) {
                promises.push(satsueiMemoCategoryService.addSatsueiMemoColumnValueList(newColumnValueData))
            }
            if (updatedColumnValueData.length > 0) {
                promises.push(satsueiMemoCategoryService.updateSatsueiMemoColumnValueList(updatedColumnValueData))
            }
            if (removedColumnValueData.length > 0) {
                promises.push(satsueiMemoCategoryService.removeSatsueiMemoColumnValueList(removedColumnValueData))
            }

            // Execute all operations
            if (promises.length > 0) {
                const responses = await Promise.all(promises)

                // Check for errors in any response
                for (const response of responses) {
                    if (handleApiResponse(response, '撮影メモの登録に失敗しました。')) {
                        return
                    }
                }
            }

            setSuccessMessage('登録が完了しました。')
            setShowSuccessDialog(true)
            setSelectedCategoryId(0)

            // Reset expanded columns state after successful registration
            setExpandedColumns('')

            // Reload data
            await loadCategories()
        } catch (error) {
            // Check if this is a validation error (thrown manually with specific validation messages)
            if (error.message && error.message.includes('撮影メモ項目に項目値が登録されていません')) {
                setSuccessMessage(error.message)
                setShowSuccessDialog(true)
            } else {
                // Handle all other errors (including timeout, network, API errors) with handleApiError
                handleApiError(error)
            }
        } finally {
            setIsRegistering(false)
        }
    }

    // Helper functions
    const getColumnTypeName = useCallback((columnType) => {
        const type = COLUMN_TYPES.find(t => t.id === columnType)
        return type ? type.value : ''
    }, [])


    // Event handlers
    const handleAddCategory = () => {
        try {
            addCategory(newCategoryName)
            setNewCategoryName('')
            setShowAddCategoryModal(false)
        } catch (error) {
            setSuccessMessage(error.message)
            setShowSuccessDialog(true)
        }
    }

    const handleAddColumn = () => {
        try {
            addColumn(selectedCategoryId, newColumnName, newColumnType)
            setNewColumnName('')
        } catch (error) {
            setSuccessMessage(error.message)
            setShowSuccessDialog(true)
        }
    }

    const handleAddColumnValue = (categoryId, columnId) => {
        try {
            addColumnValue(categoryId, columnId, newColumnValueName)
            setNewColumnValueName('')
        } catch (error) {
            setSuccessMessage(error.message)
            setShowSuccessDialog(true)
        }
    }

    const handleCategorySelect = (categoryId) => {
        setSelectedCategoryId(categoryId)
    }

    const handleColumnConfig = (categoryId, columnId, columnType) => {
        setSelectedColumnId(columnId)

        const columnKey = `${categoryId}-${columnId}`

        if ([2, 3, 4, 17].includes(parseInt(columnType))) {
            // For text/number columns, toggle the expanded state to show/hide initial value
            setExpandedColumns(expandedColumns === columnKey ? '' : columnKey)
        } else if ([5, 6, 7, 8, 11, 12, 13, 14].includes(parseInt(columnType))) {
            // For selection-based columns, toggle the expanded state
            setExpandedColumns(expandedColumns === columnKey ? '' : columnKey)
        }
    }

    const isColumnExpanded = (categoryId, columnId) => {
        return expandedColumns === `${categoryId}-${columnId}`
    }

    if (isLoadingCategories) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900" role="status" aria-label="Loading"></div>
            </div>
        )
    }

    return (
        <Layout pageTitle='撮影メモ管理'>
            <div className="min-h-screen bg-gray-50">

                {/* Main Content */}
                <div className="mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
                    <div className="grid grid-cols-1 xl:grid-cols-[4fr_6fr] gap-4 sm:gap-6 lg:gap-8">

                        {/* STEP1: Category Selection */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="bg-purple-800 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-t-lg">
                                <h2 className="text-base sm:text-lg font-semibold">STEP1. 撮影メモカテゴリーを選択</h2>
                            </div>

                            <div className="p-3 sm:p-4 lg:p-6">
                                {/* Add Category Button */}
                                <Button
                                    id="add-category-button"
                                    variant="danger"
                                    onClick={() => setShowAddCategoryModal(true)}
                                    className="mb-3 sm:mb-4 w-full sm:w-auto"
                                >
                                    追加
                                </Button>

                                {/* Category List */}
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                            onDragEnd={handleCategoryDragEnd}
                                >
                                    <SortableContext
                                        items={activeCategories.map(category => category.satsueiMemoCategoryId)}
                                    >
                                        <div className="space-y-2">
                                            {activeCategories.map((category) => (
                                                <SortableCategoryItem
                                                    key={category.satsueiMemoCategoryId}
                                                    category={category}
                                                    isSelected={selectedCategoryId === category.satsueiMemoCategoryId}
                                                    onSelect={handleCategorySelect}
                                                    onUpdateTemp={updateCategoryTemp}
                                                    onUpdateFinal={(categoryId, value) => {
                                                        try {
                                                            updateCategory(categoryId, value)
                                                        } catch (error) {
                                                            setSuccessMessage(error.message)
                                                            setShowSuccessDialog(true)
                                                        }
                                                    }}
                                                    onCancelEdit={cancelCategoryEdit}
                                                    onDelete={deleteCategory}
                                                    tempCategoryNames={tempCategoryNames}
                                                />
                                    ))}
                                </div>
                                    </SortableContext>
                                </DndContext>

                                {/* Deleted Categories Button */}
                                <Button
                                    id="deleted-categories-button"
                                    variant="secondary"
                                    onClick={() => setShowDeletedCategoryModal(true)}
                                    className="mt-3 sm:mt-4 w-full sm:w-auto"
                                >
                                    削除済
                                </Button>
                            </div>
                        </div>

                        {/* STEP2: Column Settings */}
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            <div className="bg-purple-800 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-t-lg">
                                <h2 className="text-base sm:text-lg font-semibold">STEP2. 撮影メモ項目を設定</h2>
                            </div>

                            <div className="p-3 sm:p-4 lg:p-6">
                                {/* Add Column Form */}
                                <div className="flex gap-2 mb-4 flex-col sm:flex-row">
                                    <input
                                        id="new-column-name"
                                        type="text"
                                        placeholder="撮影メモ項目名"
                                        value={newColumnName}
                                        onChange={(e) => setNewColumnName(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                    />
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <select
                                            id="new-column-type"
                                            value={newColumnType}
                                            onChange={(e) => setNewColumnType(e.target.value)}
                                            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                                        >
                                            {COLUMN_TYPES.map(type => (
                                                <option key={type.id} value={type.id}>
                                                    {type.value}
                                                </option>
                                            ))}
                                        </select>
                                        <Button
                                            id="add-column-button"
                                            variant="danger"
                                            onClick={handleAddColumn}
                                            className="w-full sm:w-auto"
                                        >
                                            追加
                                        </Button>
                                    </div>
                                </div>

                                {/* Column List */}
                                {selectedCategory && (
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                                onDragEnd={handleColumnDragEnd}
                                    >
                                        <SortableContext
                                            items={activeColumns.map(column => column.satsueiMemoColumnId)}
                                        >
                                            <div className="space-y-3 sm:space-y-4">
                                                {activeColumns.map((column) => (
                                                    <SortableColumnItem
                                                        key={column.satsueiMemoColumnId}
                                                        column={column}
                                                        selectedCategory={selectedCategory}
                                                        onUpdateTemp={updateColumnTemp}
                                                        onUpdateFinal={(categoryId, columnId, value, requiredFlg) => {
                                                            try {
                                                                updateColumn(categoryId, columnId, value, requiredFlg)
                                                            } catch (error) {
                                                                setSuccessMessage(error.message)
                                                                setShowSuccessDialog(true)
                                                            }
                                                        }}
                                                        onCancelEdit={cancelColumnEdit}
                                                        onDelete={deleteColumn}
                                                        onConfig={handleColumnConfig}
                                                        isExpanded={isColumnExpanded(selectedCategory.satsueiMemoCategoryId, column.satsueiMemoColumnId)}
                                                        getColumnTypeName={getColumnTypeName}
                                                        newColumnValueName={newColumnValueName}
                                                        setNewColumnValueName={setNewColumnValueName}
                                                        onAddColumnValue={handleAddColumnValue}
                                                        onUpdateColumnValue={updateColumnValue}
                                                        onDeleteColumnValue={deleteColumnValue}
                                                        onRestoreColumnValue={restoreColumnValue}
                                                        onUpdateColumnInitialValue={updateColumnInitialValue}
                                                        categories={categories}
                                                        setCategories={setCategories}
                                                        setSuccessMessage={setSuccessMessage}
                                                        setShowSuccessDialog={setShowSuccessDialog}
                                                        tempColumnNames={tempColumnNames}
                                                    />
                                                            ))}
                                                        </div>
                                        </SortableContext>
                                    </DndContext>
                                )}

                                {/* Deleted Columns Button */}
                                <Button
                                    id="deleted-columns-button"
                                    variant="secondary"
                                    onClick={() => setShowDeletedColumnModal(true)}
                                    className="mt-4"
                                >
                                    削除済
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Register Button */}
                    <div className="flex justify-center mt-6 sm:mt-8">
                        <Button
                            id="register-button"
                            data-testid="register-button"
                            variant="danger"
                            size="lg"
                            onClick={handleRegister}
                            loading={isRegistering}
                            className="w-full sm:w-auto"
                        >
                            登録する
                        </Button>
                    </div>
                </div>

                {/* Modals */}
                <AnimatePresence>
                    {/* Add Category Modal */}
                    {showAddCategoryModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                        >
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full mx-2 sm:mx-4"
                            >
                                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">撮影メモカテゴリーを追加する</h3>
                                <input
                                    id="new-category-name"
                                    type="text"
                                    placeholder="撮影メモカテゴリー名"
                                    value={newCategoryName}
                                    onChange={(e) => setNewCategoryName(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3 sm:mb-4 text-sm sm:text-base"
                                />
                                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 sm:space-x-3">
                                    <Button
                                        id="add-category-cancel"
                                        variant="secondary"
                                        onClick={() => setShowAddCategoryModal(false)}
                                        className="w-full sm:w-auto order-2 sm:order-1"
                                    >
                                        キャンセル
                                    </Button>
                                    <Button
                                        id="add-category-confirm"
                                        variant="danger"
                                        onClick={handleAddCategory}
                                        className="w-full sm:w-auto order-1 sm:order-2"
                                    >
                                        追加
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}

                    {/* Deleted Categories Modal */}
                    {showDeletedCategoryModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                        >
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                className="bg-white rounded-lg p-4 sm:p-6 max-w-lg w-full mx-2 sm:mx-4"
                            >
                                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">削除済み撮影メモカテゴリー</h3>
                                <div className="max-h-48 sm:max-h-64 overflow-y-auto space-y-2">
                                    {deletedCategories.map(category => (
                                        <div key={category.satsueiMemoCategoryId} className="flex items-center justify-between p-2 border rounded">
                                            <span className="text-sm sm:text-base truncate flex-1 mr-2">{category.satsueiMemoCategoryName}</span>
                                            <Button
                                                id={`restore-category-${category.satsueiMemoCategoryId}`}
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    restoreCategory(category.satsueiMemoCategoryId)
                                                }}
                                                className="flex-shrink-0"
                                            >
                                                戻す
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-end mt-3 sm:mt-4">
                                    <Button
                                        id="deleted-categories-close"
                                        variant="secondary"
                                        onClick={() => setShowDeletedCategoryModal(false)}
                                        className="w-full sm:w-auto"
                                    >
                                        閉じる
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}

                    {/* Deleted Columns Modal */}
                    {showDeletedColumnModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                        >
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                className="bg-white rounded-lg p-4 sm:p-6 max-w-lg w-full mx-2 sm:mx-4"
                            >
                                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">削除済み撮影メモ項目</h3>
                                <div className="max-h-48 sm:max-h-64 overflow-y-auto space-y-2">
                                    {deletedColumns.map(column => (
                                        <div key={column.satsueiMemoColumnId} className="flex items-center justify-between p-2 border rounded">
                                            <div className="flex-1 min-w-0 mr-2">
                                                <span className="font-medium text-sm sm:text-base block truncate">{column.satsueiMemoColumnName}</span>
                                                <span className="text-xs sm:text-sm text-gray-500">({getColumnTypeName(parseInt(column.columnType))})</span>
                                            </div>
                                            <Button
                                                id={`restore-column-${column.satsueiMemoColumnId}`}
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    restoreColumn(selectedCategoryId, column.satsueiMemoColumnId)
                                                }}
                                                className="flex-shrink-0"
                                            >
                                                戻す
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-end mt-3 sm:mt-4">
                                    <Button
                                        id="deleted-columns-close"
                                        variant="secondary"
                                        onClick={() => setShowDeletedColumnModal(false)}
                                        className="w-full sm:w-auto"
                                    >
                                        閉じる
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Error Dialog */}
                <MessageDialog
                    isOpen={showErrorDialog}
                    onClose={closeErrorDialog}
                    title="エラー"
                    message={errorMessage}
                    type="error"
                />

                {/* Success Dialog */}
                <MessageDialog
                    isOpen={showSuccessDialog}
                    onClose={() => setShowSuccessDialog(false)}
                    title="メッセージ"
                    message={successMessage}
                    type="info"
                />
            </div>
        </Layout>
    )
}

export default SatsueiMemoCategory
